import camelcaseKeys from "camelcase-keys";
import { createContext, useContext, useEffect, useState } from "react";

import { useUserServiceSession } from "hooks/user/base";

export type User = {
  id: string;
  oid: string;
  upn: string;
  role: "support" | "approver" | "admin";
  email: string;
  status: "active" | "pending" | "suspended" | "banned" | "deleted";
  lastName: string;
  firstName: string;
  lastActive: Date | null;
  session: {
    id: "";
    status: "active" | "inactive";
    expiresAt: null;
  };
  project: {
    connected: boolean;
    id: string;
    uri: string;
    name: string;
    updated: string;
    assigned: string[];
    autoDisconnect: boolean;
  };
};

export const INIT_USER: User = {
  id: "",
  oid: "",
  upn: "",
  role: "support",
  email: "",
  status: "pending",
  lastName: "",
  firstName: "",
  lastActive: null,
  session: {
    id: "",
    status: "inactive",
    expiresAt: null,
  },
  project: {
    connected: false,
    id: "",
    uri: "",
    name: "",
    updated: "",
    assigned: [""],
    autoDisconnect: false,
  },
};

const AuthContext = createContext({
  user: INIT_USER,
  auth: false,
  isInitial: true,
  isLoading: false,
  setUser: (user: User) => {},
  setAuth: (auth: boolean) => {},
});

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }: { children: any }) => {
  const [auth, setAuth] = useState<boolean>(false);
  const [user, setUser] = useState<User>(INIT_USER);
  const [isInitial, setIsInitial] = useState<boolean>(true);

  const session = useUserServiceSession();
  const isLoading = session.isLoading;

  useEffect(() => {
    // check server for active session
    session.mutate();
  }, []);

  useEffect(() => {
    if (session.isSuccess) {
      const user = camelcaseKeys(session.data.data.user, { deep: true });

      setAuth(true);
      setUser(user);
      setIsInitial(false);

      localStorage.setItem("GRACE_AUTHENTICATED", "true");
    }
  }, [session.isSuccess]);

  return (
    <AuthContext.Provider
      value={{ auth, setAuth, user, setUser, isLoading, isInitial }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
